import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Fab,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Group as GroupIcon,
  CardMembership as PassportIcon,
  LocalHospital as EmergencyIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Description as DocumentIcon,
  Visibility as ViewIcon,
  PhotoCamera as PhotoIcon,
  Close as CloseIcon,
  LocalHospital as MedicalIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { travelersAPI, apiCall } from '../services/api';
import PDFViewer from '../components/PDFViewer';

const TravelerProfile = () => {
  const { uuid } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [traveler, setTraveler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [documentDialog, setDocumentDialog] = useState({ open: false, document: null, title: '', url: '' });

  useEffect(() => {
    if (uuid) {
      fetchTravelerProfile();
    }
  }, [uuid]);

  const fetchTravelerProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiCall(travelersAPI.getByUUID, uuid);
      setTraveler(data.traveler);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatPhoneNumbers = (phoneString) => {
    if (!phoneString) return 'Not provided';
    // Handle multiple phone numbers separated by commas, dashes, or spaces
    return phoneString.split(/[,\-\s]+/).filter(Boolean).join(' • ');
  };

  const getPassportStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'Unknown', color: 'default' };
    
    try {
      const expiry = parseISO(expiryDate);
      const today = new Date();
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) {
        return { status: 'Expired', color: 'error' };
      } else if (daysUntilExpiry < 180) {
        return { status: `${daysUntilExpiry} days left`, color: 'warning' };
      } else {
        return { status: 'Valid', color: 'success' };
      }
    } catch {
      return { status: 'Invalid date', color: 'error' };
    }
  };

  const handleViewDocument = (document, title) => {
    // Show document in secure dialog using the new PDF viewer
    setDocumentDialog({ 
      open: true, 
      document, 
      title,
      url: `${process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:3001' : '')}/api/documents/public/${document.id}/view/${traveler.uuid}`
    });
  };

  const handleCloseDocument = () => {
    setDocumentDialog({ open: false, document: null, title: '', url: '' });
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          p: 2,
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="h6" gutterBottom>
            Traveler Profile Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The QR code may be invalid or the traveler profile may have been removed.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!traveler) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Traveler Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No traveler profile found for this QR code.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const passportStatus = getPassportStatus(traveler.passport_expiry);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        pb: 8, // Space for FAB
        userSelect: 'text', // Allow text selection for readability
        '& *': {
          // Disable drag and drop for images and other elements
          WebkitUserDrag: 'none',
          userDrag: 'none',
        }
      }}
      onContextMenu={(e) => {
        // Disable right-click context menu on document areas
        if (e.target.closest('[data-document]')) {
          e.preventDefault();
        }
      }}
    >
      {/* Header */}
      <Paper
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: 4,
          borderRadius: 0,
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
          position: 'relative',
        }}
      >
        {/* Company Logo */}
        {traveler.logos?.company && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: { xs: 80, md: 120 },
              height: { xs: 40, md: 60 },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img
              src={`${process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:3001' : '')}/api/logos/view/company/${traveler.logos.company.path.split('/').pop()}`}
              alt="Company Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)', // Make logo white on colored background
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Tour Logo */}
        {traveler.logos?.tour && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: { xs: 80, md: 120 },
              height: { xs: 40, md: 60 },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img
              src={`${process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:3001' : '')}/api/logos/view/tour/${traveler.logos.tour.path.split('/').pop()}`}
              alt="Tour Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)', // Make logo white on colored background
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems="center" gap={3}>
          <Avatar
            sx={{
              width: { xs: 120, md: 140 },
              height: { xs: 120, md: 140 },
              bgcolor: traveler.documents?.profile_photo ? 'transparent' : 'rgba(255,255,255,0.2)',
              fontSize: '3rem',
              border: '4px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
            src={traveler.documents?.profile_photo ? `${process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost') ? 'http://localhost:3001' : '')}/api/documents/public/${traveler.documents.profile_photo.id}/view/${traveler.uuid}` : undefined}
            onDragStart={(e) => e.preventDefault()} // Prevent dragging
            onContextMenu={(e) => e.preventDefault()} // Prevent right-click save
          >
            {!traveler.documents?.profile_photo && (
              traveler.gender === 'Male' ? (
                <MaleIcon sx={{ fontSize: '4rem' }} />
              ) : (
                <FemaleIcon sx={{ fontSize: '4rem' }} />
              )
            )}
          </Avatar>
          
          <Box flex={1} textAlign={isMobile ? 'center' : 'left'}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
              {traveler.name}
            </Typography>
            
            {traveler.arabic_name && (
              <Typography variant="h5" sx={{ opacity: 0.95, mb: 1, fontWeight: 500, direction: 'rtl' }}>
                {traveler.arabic_name}
              </Typography>
            )}
            
            {traveler.dhivehi_name && (
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2, fontWeight: 400 }}>
                {traveler.dhivehi_name}
              </Typography>
            )}
            
            <Box display="flex" gap={1} mt={2} flexWrap="wrap" justifyContent={isMobile ? 'center' : 'flex-start'}>
              <Chip
                label={`${traveler.gender}, ${traveler.age} years`}
                size="medium"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
              />
              {traveler.hajji_no && (
                <Chip
                  label={`Hajji #${traveler.hajji_no}`}
                  size="medium"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
        
        {/* Tour Information */}
        {traveler.tour_name && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FlightIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Tour Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {traveler.tour_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {traveler.tour_destination}
                  </Typography>
                </Grid>
                
                {(traveler.tour_start_date || traveler.tour_end_date) && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Tour Dates:</strong> {formatDate(traveler.tour_start_date)} - {formatDate(traveler.tour_end_date)}
                    </Typography>
                  </Grid>
                )}
                
                {traveler.group_number && (
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Group {traveler.group_number}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {traveler.bus && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Bus:</strong> {traveler.bus}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Accommodation */}
        {(traveler.makkah_room || traveler.madina_room || traveler.makkah_hotel || traveler.madina_hotel) && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <HotelIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Accommodation
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {(traveler.makkah_hotel || traveler.makkah_room) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Makkah
                    </Typography>
                    {traveler.makkah_hotel && (
                      <Typography variant="body2">{traveler.makkah_hotel}</Typography>
                    )}
                    {traveler.makkah_room && (
                      <Typography variant="body2">Room: {traveler.makkah_room}</Typography>
                    )}
                  </Grid>
                )}
                
                {(traveler.madina_hotel || traveler.madina_room) && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Madina
                    </Typography>
                    {traveler.madina_hotel && (
                      <Typography variant="body2">{traveler.madina_hotel}</Typography>
                    )}
                    {traveler.madina_room && (
                      <Typography variant="body2">Room: {traveler.madina_room}</Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Flight Information */}
        {(traveler.departure_flight || traveler.return_flight) && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FlightIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Flight Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {traveler.departure_flight && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Departure
                    </Typography>
                    <Typography variant="body2">{traveler.departure_flight}</Typography>
                  </Grid>
                )}
                
                {traveler.return_flight && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Return
                    </Typography>
                    <Typography variant="body2">{traveler.return_flight}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PhoneIcon color="primary" />
              <Typography variant="h6" component="h2">
                Contact Information
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {traveler.phone && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatPhoneNumbers(traveler.phone)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {traveler.saudi_phone && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Saudi Phone:</strong> {traveler.saudi_phone}
                  </Typography>
                </Grid>
              )}
              
              {traveler.emergency_contact && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmergencyIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Emergency:</strong> {traveler.emergency_contact}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Passport Information */}
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PassportIcon color="primary" />
              <Typography variant="h6" component="h2">
                Passport Information
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Passport Number:</strong><br />
                  {traveler.passport_no}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Expiry Date:</strong><br />
                  {formatDate(traveler.passport_expiry)}
                </Typography>
                <Chip
                  label={passportStatus.status}
                  color={passportStatus.color}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Grid>
              
              {traveler.passport_issued && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Issued:</strong> {formatDate(traveler.passport_issued)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <DocumentIcon color="primary" />
              <Typography variant="h6" component="h2">
                Documents
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card 
                  data-document="visa"
                  sx={{ 
                    cursor: traveler.documents?.visa ? 'pointer' : 'default',
                    border: '2px solid',
                    borderColor: traveler.documents?.visa ? 'success.main' : 'divider',
                    '&:hover': traveler.documents?.visa ? { borderColor: 'success.dark' } : {}
                  }}
                  onClick={() => traveler.documents?.visa && handleViewDocument(traveler.documents.visa, 'Visa Document')}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <DocumentIcon color={traveler.documents?.visa ? 'success' : 'disabled'} />
                        <Box>
                          <Typography variant="subtitle2">Visa Document</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {traveler.documents?.visa ? 'Click to view' : 'Missing'}
                          </Typography>
                        </Box>
                      </Box>
                      {traveler.documents?.visa && <ViewIcon color="primary" />}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card 
                  data-document="passport"
                  sx={{ 
                    cursor: traveler.documents?.passport ? 'pointer' : 'default',
                    border: '2px solid',
                    borderColor: traveler.documents?.passport ? 'success.main' : 'divider',
                    '&:hover': traveler.documents?.passport ? { borderColor: 'success.dark' } : {}
                  }}
                  onClick={() => traveler.documents?.passport && handleViewDocument(traveler.documents.passport, 'Passport Copy')}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <PassportIcon color={traveler.documents?.passport ? 'success' : 'disabled'} />
                        <Box>
                          <Typography variant="subtitle2">Passport Copy</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {traveler.documents?.passport ? 'Click to view' : 'Missing'}
                          </Typography>
                        </Box>
                      </Box>
                      {traveler.documents?.passport && <ViewIcon color="primary" />}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Medical Information Document - Only show if exists */}
              {traveler.documents?.medical && (
                <Grid item xs={12} sm={6}>
                  <Card
                    data-document="medical"
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'success.main',
                      '&:hover': { borderColor: 'success.dark' }
                    }}
                    onClick={() => handleViewDocument(traveler.documents.medical, 'Medical Information')}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <MedicalIcon color="success" />
                          <Box>
                            <Typography variant="subtitle2">Medical Information</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click to view
                            </Typography>
                          </Box>
                        </Box>
                        <ViewIcon color="primary" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Address Information */}
        {(traveler.permanent_address_dhivehi || traveler.current_address_dhivehi) && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocationIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Address Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {traveler.permanent_address_dhivehi && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Permanent Address
                    </Typography>
                    <Typography variant="body2">{traveler.permanent_address_dhivehi}</Typography>
                    {traveler.permanent_atoll && traveler.permanent_island && (
                      <Typography variant="caption" color="text.secondary">
                        {traveler.permanent_island}, {traveler.permanent_atoll}
                      </Typography>
                    )}
                  </Grid>
                )}
                
                {traveler.current_address_dhivehi && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Current Address
                    </Typography>
                    <Typography variant="body2">{traveler.current_address_dhivehi}</Typography>
                    {traveler.current_atoll && traveler.current_island && (
                      <Typography variant="caption" color="text.secondary">
                        {traveler.current_island}, {traveler.current_atoll}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contact */}
        {traveler.tour_emergency_contact && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <EmergencyIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Tour Emergency Contact
                </Typography>
              </Box>
              <Typography variant="body2">
                {traveler.tour_emergency_contact}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Additional Information */}
        {(traveler.mahram || traveler.medical || traveler.care_of || traveler.guide_info) && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <InfoIcon color="primary" />
                <Typography variant="h6" component="h2">
                  Additional Information
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {traveler.mahram && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Mahram:</strong> {traveler.mahram}
                    </Typography>
                  </Grid>
                )}
                
                {traveler.medical === 'Yes' && (
                  <Grid item xs={12}>
                    <Chip label="Medical Requirements" color="warning" size="small" />
                  </Grid>
                )}
                
                {traveler.care_of && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Care Of:</strong> {traveler.care_of}
                    </Typography>
                  </Grid>
                )}
                
                {traveler.guide_info && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Guide Information:</strong> {traveler.guide_info}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Last Updated */}
        <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.background.paper, borderRadius: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Profile last updated: {format(lastUpdated, 'MMM dd, yyyy HH:mm')}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            UUID: {traveler.uuid}
          </Typography>
        </Paper>
      </Box>

      {/* Floating Refresh Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={fetchTravelerProfile}
        disabled={loading}
      >
        <RefreshIcon />
      </Fab>

      {/* Document Viewer Dialog */}
      <Dialog
        open={documentDialog.open}
        onClose={handleCloseDocument}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{documentDialog.title}</Typography>
            <IconButton onClick={handleCloseDocument}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh', display: 'flex', flexDirection: 'column' }}>
          {documentDialog.document ? (
            <PDFViewer
              documentUrl={documentDialog.url}
              travelerUuid={traveler?.uuid}
              documentId={documentDialog.document.id}
              title={documentDialog.title}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Preparing secure document viewer...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Documents are view-only and cannot be downloaded for security purposes
          </Typography>
          <Button onClick={handleCloseDocument} color="primary" variant="contained">
            Close Viewer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TravelerProfile;